import type { ReactNode } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Plus, X } from 'lucide-react';
import {
  addGroupBy,
  addMeasure,
  availableAggs,
  numericColumns,
  parseEdges,
  removeGroupBy,
  removeMeasure,
  setBucketEdges,
  setDateGranularity,
  setGroupByColumn,
  setMeasureAgg,
  setPivotColumn,
  setSubtotals,
  toggleDetailColumn,
  updateMeasure,
} from '../../reporting/definitionUpdates';
import {
  NO_RELATED,
  refLabel,
  resolveColumnMeta,
  type RelatedManifests,
} from '../../reporting/columnRefs';
import { optionGroups, renderOptionGroups } from './columnOptions';
import type {
  Agg,
  DatasetManifestEntry,
  DateGranularity,
  ReportDefinition,
} from '../../types/reporting';

const AGG_LABELS: Record<Agg, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
};

const GRANULARITIES: { value: DateGranularity; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'quarter', label: 'Quarter' },
];

// Standard AR aging buckets: 0-30 / 31-60 / 61-90 / 91+.
const AGING_EDGES = [31, 61, 91];

// Upper bound on stacked group-by dimensions. The compiler builds N dimensions,
// the chart composites them into one capped category axis, and the pivot/table
// render whatever compiles — so the ceiling is a UX sanity limit, not a
// technical one. Four keeps a report legible without an unbounded dimension
// explosion.
const MAX_GROUP_BYS = 4;

interface FieldPanelProps {
  dataset: DatasetManifestEntry;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
  related?: RelatedManifests;
}

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <label className='text-xs font-semibold uppercase text-muted-foreground'>
    {children}
  </label>
);

const FieldPanel = ({
  dataset,
  definition,
  onChange,
  related = NO_RELATED,
}: FieldPanelProps) => {
  const numberColumns = numericColumns(dataset);
  const aggs = availableAggs(dataset);
  const grouped = definition.group_bys.length > 0;
  // Aggregate intent: a group-by, or a measure that outlived its group-by (the
  // invalid Sum/Avg-without-grouping state). Showing the measures panel whenever
  // a measure exists keeps that offending measure visible and removable instead
  // of stranding it under the detail-columns view while the results panel shows
  // the "needs a group-by or pivot" notice.
  const aggregateIntent = grouped || definition.measures.length > 0;
  // addMeasure returns the definition unchanged when every candidate measure is
  // already present; use that to hide the no-op "Add measure" button.
  const canAddMeasure = addMeasure(definition, dataset) !== definition;
  // A grouped report needs at least one measure, so removing the sole measure is
  // instantly restored — hide its remove control rather than offer a no-op. When
  // ungrouped, the sole measure is the invalid lingering one, so allow removing
  // it to return to the detail-columns view.
  const canRemoveMeasure = definition.measures.length > 1 || !grouped;
  // Whether any measure is additive (count/sum) — the only aggregates a
  // subtotal row can sum. Gates the subtotals toggle.
  const hasAdditiveMeasure = definition.measures.some(
    m => m.agg === 'count' || m.agg === 'sum'
  );

  return (
    <div className='space-y-5'>
      {!aggregateIntent && (
        <div className='space-y-1.5'>
          <SectionLabel>Columns</SectionLabel>
          {dataset.columns.map(column => (
            <div key={column.name} className='flex items-center gap-2 text-sm'>
              <Checkbox
                checkboxSize='sm'
                checked={
                  definition.columns.length === 0 ||
                  definition.columns.includes(column.name)
                }
                onCheckedChange={() =>
                  onChange(toggleDetailColumn(definition, column.name, dataset))
                }
                id={`col-${column.name}`}
              />
              <label htmlFor={`col-${column.name}`}>{column.label}</label>
            </div>
          ))}
          {/* One group per JOINED related dataset (the parent gates `related`
              to definition.joined_datasets); columns appear only after the
              user adds the dataset as a join, not as a per-column entry point. */}
          {[...related.entries()].map(([key, entry]) => (
            <div key={key} className='space-y-1.5 pt-2'>
              <SectionLabel>{entry.label}</SectionLabel>
              {entry.columns.map(column => {
                const ref = `${key}.${column.name}`;
                return (
                  <div key={ref} className='flex items-center gap-2 text-sm'>
                    <Checkbox
                      checkboxSize='sm'
                      // Related columns are never part of the "empty = all"
                      // default; they are only on when explicitly chosen.
                      checked={definition.columns.includes(ref)}
                      onCheckedChange={() =>
                        onChange(toggleDetailColumn(definition, ref, dataset))
                      }
                      id={`col-${ref}`}
                    />
                    <label htmlFor={`col-${ref}`}>{column.label}</label>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className='space-y-2'>
        <SectionLabel>Group by</SectionLabel>
        {definition.group_bys.map((groupBy, index) => {
          const meta = resolveColumnMeta(groupBy.column, dataset, related);
          const columnLabel = refLabel(groupBy.column, dataset, related);
          return (
            <div key={index} className='space-y-1 rounded-md border p-2'>
              <div className='flex items-center gap-1'>
                <Select
                  value={groupBy.column}
                  onValueChange={value =>
                    onChange(
                      setGroupByColumn(
                        definition,
                        index,
                        value,
                        dataset,
                        related
                      )
                    )
                  }
                >
                  <SelectTrigger
                    selectSize='sm'
                    className='flex-1'
                    title={columnLabel}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Hide columns another group-by already uses so a switch
                        can't create two dimensions with the same alias. */}
                    {renderOptionGroups(
                      optionGroups(
                        dataset,
                        related,
                        ref =>
                          ref === groupBy.column ||
                          !definition.group_bys.some(
                            (g, i) => i !== index && g.column === ref
                          )
                      )
                    )}
                  </SelectContent>
                </Select>
                <Button
                  size='sm'
                  variant='ghost'
                  aria-label={`Remove ${columnLabel} grouping`}
                  onClick={() => onChange(removeGroupBy(definition, index))}
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>
              {meta?.type === 'number' && (
                <div className='space-y-1'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() =>
                      onChange(setBucketEdges(definition, index, AGING_EDGES))
                    }
                  >
                    0-30 · 31-60 · 61-90 · 91+
                  </Button>
                  <Label htmlFor={`ranges-${index}`}>Ranges</Label>
                  <Input
                    id={`ranges-${index}`}
                    key={
                      groupBy.transform?.type === 'bucket'
                        ? groupBy.transform.edges.join(',')
                        : 'exact'
                    }
                    inputSize='sm'
                    placeholder='e.g. 31, 61, 91'
                    defaultValue={
                      groupBy.transform?.type === 'bucket'
                        ? groupBy.transform.edges.join(', ')
                        : ''
                    }
                    onBlur={e =>
                      onChange(
                        setBucketEdges(
                          definition,
                          index,
                          parseEdges(e.target.value)
                        )
                      )
                    }
                  />
                  <p className='text-xs text-muted-foreground'>
                    Leave blank to show exact values
                  </p>
                </div>
              )}
              {meta?.type === 'date' && (
                <Select
                  value={
                    groupBy.transform?.type === 'date_trunc'
                      ? groupBy.transform.unit
                      : 'exact'
                  }
                  onValueChange={value =>
                    onChange(
                      setDateGranularity(
                        definition,
                        index,
                        value === 'exact' ? null : (value as DateGranularity)
                      )
                    )
                  }
                >
                  <SelectTrigger selectSize='sm'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='exact'>Exact date</SelectItem>
                    {GRANULARITIES.map(g => (
                      <SelectItem key={g.value} value={g.value}>
                        By {g.label.toLowerCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
        {definition.group_bys.length < MAX_GROUP_BYS && (
          <Select
            value=''
            onValueChange={value =>
              onChange(addGroupBy(definition, value, dataset, related))
            }
          >
            <SelectTrigger selectSize='sm'>
              <SelectValue placeholder='+ Add group by' />
            </SelectTrigger>
            <SelectContent>
              {/* Don't offer a column that's already a group-by — a second
                  dimension on it would compile to a duplicate result column. */}
              {renderOptionGroups(
                optionGroups(
                  dataset,
                  related,
                  ref => !definition.group_bys.some(g => g.column === ref)
                )
              )}
            </SelectContent>
          </Select>
        )}
        {definition.group_bys.length >= 2 && (
          <div className='space-y-1 pt-1'>
            {/* Muted text-xs (vs the full-contrast text-sm column checkboxes):
                this is a secondary option toggle, not section content — Chris
                flagged the louder first pass as competing with the group-bys. */}
            <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <Checkbox
                checkboxSize='sm'
                id='subtotals-toggle'
                checked={!!definition.subtotals}
                // Subtotal rows sum count/sum measures; with only avg/min/max
                // there is nothing meaningful to show, so the toggle waits for
                // an additive measure instead of enabling an all-blank row.
                // BUT an already-enabled flag stays uncheckable: disabling a
                // checked box would strand subtotals: true dormant in the
                // definition with no way to turn it off.
                disabled={!hasAdditiveMeasure && !definition.subtotals}
                onCheckedChange={checked =>
                  onChange(setSubtotals(definition, checked === true))
                }
              />
              <label
                className='cursor-pointer leading-4'
                htmlFor='subtotals-toggle'
              >
                Show subtotals by{' '}
                {refLabel(definition.group_bys[0].column, dataset, related)}
              </label>
            </div>
            {!hasAdditiveMeasure && (
              <p className='text-xs text-muted-foreground'>
                Add a Count or Sum value to calculate subtotals.
              </p>
            )}
            {definition.view === 'bar' && !!definition.subtotals && (
              <p className='text-xs text-muted-foreground'>
                Subtotals appear in the table view only.
              </p>
            )}
          </div>
        )}
      </div>

      {aggregateIntent && (
        <div className='space-y-5'>
          {/* Show values and Break out into columns are two distinct sections;
              give each its own space-y-2 block inside the space-y-5 wrapper so
              the "Break out into columns" label gets the same ~20px breathing
              room above it as every other section label, instead of sitting
              8px under the Add value button. */}
          <div className='space-y-2'>
            {/* Measures stay primary-only in v1 (no related dataset has numeric columns). */}
            <SectionLabel>Show values</SectionLabel>
            {numberColumns.length === 0 && (
              <p className='text-xs text-muted-foreground'>
                {dataset.label} can only be counted
              </p>
            )}
            {definition.measures.map((measure, index) => (
              <div key={index} className='flex items-center gap-1'>
                <Select
                  value={measure.agg}
                  onValueChange={value =>
                    onChange(
                      setMeasureAgg(definition, index, value as Agg, dataset)
                    )
                  }
                >
                  <SelectTrigger selectSize='sm' className='w-24'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {aggs.map(a => (
                      <SelectItem key={a} value={a}>
                        {AGG_LABELS[a]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {measure.agg !== 'count' && (
                  <Select
                    value={measure.column ?? ''}
                    onValueChange={value =>
                      onChange(
                        updateMeasure(definition, index, {
                          agg: measure.agg,
                          column: value,
                        })
                      )
                    }
                  >
                    <SelectTrigger selectSize='sm' className='flex-1'>
                      <SelectValue placeholder='Column' />
                    </SelectTrigger>
                    <SelectContent>
                      {numberColumns
                        // Hide a column another measure already aggregates the same
                        // way (same agg+column → same alias → one merged column).
                        .filter(
                          c =>
                            c.name === measure.column ||
                            !definition.measures.some(
                              (m, i) =>
                                i !== index &&
                                m.agg === measure.agg &&
                                m.column === c.name
                            )
                        )
                        .map(c => (
                          <SelectItem key={c.name} value={c.name}>
                            {c.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
                {canRemoveMeasure && (
                  <Button
                    size='sm'
                    variant='ghost'
                    aria-label='Remove value'
                    onClick={() => onChange(removeMeasure(definition, index))}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
            ))}
            {canAddMeasure && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => onChange(addMeasure(definition, dataset))}
              >
                <Plus className='h-4 w-4' /> Add value
              </Button>
            )}
          </div>

          <div className='space-y-2'>
            <SectionLabel>Break out into columns</SectionLabel>
            <Select
              value={definition.pivot_column?.column ?? 'none'}
              onValueChange={value =>
                onChange(
                  setPivotColumn(
                    definition,
                    value === 'none' ? null : value,
                    dataset,
                    related
                  )
                )
              }
            >
              <SelectTrigger selectSize='sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>None</SelectItem>
                {renderOptionGroups(optionGroups(dataset, related))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldPanel;
